import React, { useMemo, useState } from "react";
import { calcEPF, formatINR } from "../../lib/finance";
import { Slider, InputRow, ResultRow, SplitBar, RightPanel, Divider, HeroValue } from "./CalcUI";

export const EPFTab = () => {
  const [basic, setBasic] = useState(30000);
  const [growth, setGrowth] = useState(8);
  const [rate, setRate] = useState(8.25);
  const [years, setYears] = useState(30);

  const result = useMemo(() => calcEPF(basic, growth, rate, years), [basic, growth, rate, years]);
  const animKey = `epf-${Math.round(result.maturity)}-${basic}-${growth}-${rate}-${years}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="p-6 sm:p-10">
        <InputRow
          label="Monthly Basic Salary"
          valueText={`Rs. ${formatINR(basic)}`}
          valueTestId="epf-basic-value"
          finePrint={<><span>Rs.10,000</span><span>Rs.5,00,000</span></>}
        >
          <Slider value={basic} min={10000} max={500000} step={1000} onChange={setBasic} ariaLabel="EPF monthly basic" />
        </InputRow>

        <InputRow
          label="Annual Salary Growth"
          valueText={`${growth.toFixed(1)}% per year`}
          valueTestId="epf-growth-value"
          finePrint={<><span>0%</span><span>15% per year</span></>}
        >
          <Slider value={growth} min={0} max={15} step={0.5} onChange={setGrowth} ariaLabel="EPF salary growth" />
        </InputRow>

        <InputRow
          label="EPF Interest Rate (FY 2025-26: 8.25%)"
          valueText={`${rate.toFixed(2)}% per annum`}
          valueTestId="epf-rate-value"
          finePrint={<><span>5%</span><span>12% per annum</span></>}
        >
          <Slider value={rate} min={5} max={12} step={0.05} onChange={setRate} ariaLabel="EPF interest rate" />
        </InputRow>

        <InputRow
          label="Years of Service"
          valueText={`${years} ${years === 1 ? "Year" : "Years"}`}
          valueTestId="epf-years-value"
          finePrint={<><span>1 year</span><span>40 years</span></>}
        >
          <Slider value={years} min={1} max={40} step={1} onChange={setYears} ariaLabel="EPF years" />
        </InputRow>

        <p style={{ color: "#718096", fontSize: 11, lineHeight: 1.5 }}>
          You contribute 12% of basic; your employer contributes 12%, of which 8.33% (on basic up to the
          Rs.25,000 wage ceiling, effective 17 Sep 2026) goes to the pension scheme (EPS) and the rest to EPF.
          Interest is calculated monthly and credited yearly, as EPFO does. Assumes contributions on full basic
          and that this rate stays the same. Withdrawals after 5 years of service are tax-free; interest on
          employee contributions above Rs.2.5 lakh a year is taxable.
        </p>
      </div>

      <RightPanel testId="epf-results">
        <HeroValue label="EPF Corpus at Retirement" value={`Rs. ${formatINR(result.maturity)}`} animKey={animKey} testId="epf-maturity-value" />
        <Divider />
        <div className="space-y-3">
          <ResultRow label="Total Contribution" value={`Rs. ${formatINR(result.invested)}`} testId="epf-row-invested" />
          <ResultRow label="Interest Earned" value={`Rs. ${formatINR(result.gains)}`} valueColor="#1D9E75" testId="epf-row-gains" />
          <ResultRow label="Total Corpus" value={`Rs. ${formatINR(result.maturity)}`} testId="epf-row-total" />
        </div>
        <Divider />
        <SplitBar
          leftPct={result.investedPct}
          rightPct={result.gainsPct}
          leftLabel="Contributed"
          rightLabel="Interest"
          leftColor="#F4A700"
          rightColor="#1D9E75"
          testId="epf-split-bar"
        />
      </RightPanel>
    </div>
  );
};
